import { useState, useEffect, useRef, useCallback } from 'react';
import { apiService } from '../services/api';

/**
 * Hook to manage conversation/session state and chat history
 * Handles session loading, history fetching, and suggestions
 */
export function useConversation(projectId: string | null) {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [conversationHistory, setConversationHistory] = useState<any[]>([]);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const lastHistoryFetch = useRef<number>(0);

  const fetchHistory = useCallback(async (sid?: string) => {
    const targetSessionId = sid || sessionId;
    if (!targetSessionId) return;

    // Prevent duplicate fetches within 300ms
    const now = Date.now();
    if (now - lastHistoryFetch.current < 300) {
      console.log('Skipping duplicate history fetch');
      return;
    }
    lastHistoryFetch.current = now;

    try {
      const response = await apiService.get(`/editor/session/${targetSessionId}/history`);
      if (response.data.success) {
        setConversationHistory(response.data.history || []);
      }
    } catch (err) {
      console.error('Failed to fetch history:', err);
    }
  }, [sessionId]);

  const fetchSuggestions = useCallback(async (sid?: string) => {
    const targetSessionId = sid || sessionId;
    if (!targetSessionId) return;

    setLoadingSuggestions(true);
    try {
      const response = await apiService.get(`/editor/session/${targetSessionId}/suggestions`);
      if (response.data.success) {
        setSuggestions(response.data.suggestions || []);
        console.log('Fetched suggestions:', response.data.suggestions);
      }
    } catch (err) {
      console.error('Failed to fetch suggestions:', err);
    } finally {
      setLoadingSuggestions(false);
    }
  }, [sessionId]);

  const handleClearHistory = useCallback(async (
    onSuccess: () => void,
    onError: (message: string) => void
  ) => {
    if (!sessionId) return;

    try {
      const response = await apiService.delete(`/editor/session/${sessionId}/history`);
      if (response.data.success) {
        // Create new session and reset state
        const newSessionResponse = await apiService.get(`/editor/session?projectId=${projectId}`);
        if (newSessionResponse.data.success) {
          setSessionId(newSessionResponse.data.sessionInfo.sessionId);
          setConversationHistory([]);
          console.log('New session created:', newSessionResponse.data.sessionInfo.sessionId);
          
          // Fetch suggestions for new session
          fetchSuggestions(newSessionResponse.data.sessionInfo.sessionId);
        }
        onSuccess();
      }
    } catch (err) {
      console.error('Failed to clear history:', err);
      onError('Failed to start new conversation');
    }
  }, [sessionId, projectId, fetchSuggestions]);

  // Load session when projectId is set
  useEffect(() => {
    if (!projectId) return;

    const loadSession = async () => {
      try {
        const response = await apiService.get(`/editor/session?projectId=${projectId}`);
        if (response.data.success) {
          setSessionId(response.data.sessionInfo.sessionId);
          setConversationHistory(response.data.history || []);
          console.log('Loaded session:', response.data.sessionInfo.sessionId);
        }
      } catch (err) {
        console.error('Failed to load session:', err);
      }
    };

    loadSession();
  }, [projectId]);

  // Fetch suggestions when session ID changes
  useEffect(() => {
    if (sessionId) {
      fetchSuggestions();
    }
  }, [sessionId]);

  return {
    sessionId,
    setSessionId,
    conversationHistory,
    setConversationHistory,
    suggestions,
    setSuggestions,
    loadingSuggestions,
    fetchHistory,
    fetchSuggestions,
    handleClearHistory
  };
}

